import React, {useContext, useEffect, useState, useMemo} from 'react';
import {NavigationContext} from '../../context/navigation-context';
import {LangContext} from '../../context/lang-context';
import {NotificationContext} from '../../context/notification-context';
import useHttp from '../../hooks/use-http';
import useForm from '../../hooks/form-hook'
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import User from '../../components/Definitions/User';
import {validateEmail, validateLogin} from '../../util/dataValidation';
import fetchMSSQLHook from '../../hooks/fetchMSSQL-hook';
import UsersTable from './components/UsersTable.js'
import UsersForm from './components/UsersForm';
import SearchFilter from '../../components/Filter/SearchFilter.js';

const Users = () => {
  const navigationCtx = useContext(NavigationContext);
  const notificationCtx = useContext(NotificationContext);
  const { TRL_Pack } = useContext(LangContext);
  const { sendRequest } = useHttp();
  const { fetchMssqlApi } = fetchMSSQLHook();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showModalUser, setShowModalUser] = useState(false);
  const [modifiedUserId, setModifiedUserId] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouse, setWarehouse] = useState([]);
  const [all, setAll] = useState([]);
  const { form, openForm, closeForm } = useForm();
  const [warning, setWarning] = useState({ status: false, message: "" });

  const getWarehouses = () => {
    if (process.env.REACT_APP_LOCAL_DB === "false") {
      fetchMssqlApi('/warehouses-list', {}, (warehousesRes) => setWarehouses(warehousesRes))
    } else {
      sendRequest({ url: 'warehouse' }, (warehouseRes) => {setWarehouse(warehouseRes.data);});
    }
  };

  const getRoles = () => {
    (process.env.REACT_APP_LOCAL_DB === "false")
      ? fetchMssqlApi('/roles', {}, (rolesRes) => setRoles(rolesRes))
      : sendRequest({ url: 'roles' }, (rolesRes) => {
        setRoles(rolesRes.data);
      });
  };

  const defaultFilter = {
    name: "users",
    page: 1,
    rowsPerPage: 50,
    userId: null,
    userName: "",
    roleId: null,
    roleName: "",
    warehouseId: null,
    warehouseName: "",
    isActive: "true",
  };

  const [filter, setFilter] = useState(defaultFilter);
  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 50,
  });

  const rowsPerPage = settingsFilter.rowsPerPage || filter.rowsPerPage || 50;

  const returnFilterWithoutVisibleParams = (obj) => {
    const { visible, page, ...other } = obj;
    return other;
  };

  const [resetBtnVis, setResetBtnVis] = useState(false);

  useEffect(() => {
    (process.env.REACT_APP_LOCAL_DB === "false") &&
      (JSON.stringify(returnFilterWithoutVisibleParams(filter)) !== JSON.stringify(returnFilterWithoutVisibleParams(defaultFilter)))
      ? setResetBtnVis(true)
      : setResetBtnVis(false);
  }, [filter]);

  const parseThreeState = (val) => {
    if (val === true) return "true";
    if (val === false) return "false";
    if (val === "true") return "true";
    if (val === "false") return "false";
    if (val === 1) return "true";
    if (val === 0) return "false";
    return null;
  };

  const buildUsersPayload = (f) => {
    const userIdFromNew = (f && (f.userId ?? null));
    const roleIdFromNew = (f && (f.roleId ?? null));
    const warehouseIdFromNew = (f && (f.warehouseId ?? null));
    const isActiveFromNew = (f && (f.isActive ?? "null"));

    const userIdFromOld = (f?.columns?.[0]?.value) ? +f.columns[0].value : null;
    const roleIdFromOld = (f?.columns?.[4]?.value) ? +f.columns[4].value : null;
    const warehouseIdFromOld = (f?.columns?.[3]?.value) ? +f.columns[3].value : null;
    const isActiveFromOld = (typeof f?.columns?.[2]?.selectbar !== "undefined") ? f.columns[2].selectbar : "null";

    const userId = (userIdFromNew !== null && typeof userIdFromNew !== "undefined") ? userIdFromNew : userIdFromOld;
    const roleId = (roleIdFromNew !== null && typeof roleIdFromNew !== "undefined") ? roleIdFromNew : roleIdFromOld;
    const warehouseId = (warehouseIdFromNew !== null && typeof warehouseIdFromNew !== "undefined") ? warehouseIdFromNew : warehouseIdFromOld;

    const rawIsActive = (typeof isActiveFromNew !== "undefined" && isActiveFromNew !== null)
      ? isActiveFromNew
      : isActiveFromOld;

    return {
      userId: userId || null,
      roleId: roleId || null,
      warehouseId: warehouseId || null,
      isActive: parseThreeState(rawIsActive),
    };
  };

  const getUsers = (f = filter) => {
    const effectiveFilter = (typeof f === "boolean") ? filter : f;

    if (process.env.REACT_APP_LOCAL_DB !== "true") {
      const payload = buildUsersPayload(effectiveFilter);
      fetchMssqlApi(
        "users/users-list",
        { method: "POST", data: payload, hideNotification: true },
        (res) => {
          const safe = res || [];
          setAll(safe);
        }
      );
    } else {
      sendRequest({ url: 'users' }, (usersRes) => {
        setUsers(usersRes.data || []);
      });
    }
  };

  const getAll = (data = null) => {
    const payload = data ?? buildUsersPayload(filter);
    fetchMssqlApi(
      'users/users-list',
      { method: "POST", data: payload, hideNotification: true },
      (answer) => setAll(answer || [])
    );
  };

  useEffect(() => {
    setWarning({ status: false, message: "" });
  }, [form]);

  useEffect(() => {
    navigationCtx.setHeaderData({ text: TRL_Pack.navbar.users });
    getUsers(false);
    getRoles();
    if (process.env.REACT_APP_LOCAL_DB === "false") {
      getWarehouses();
      getAll();
    }
  }, []);


  const sortRows = (a, b) => {
    try {
      if (!a || !b) return 0;
      if (!filter?.sortBy) return 0;
      if (!all || !all.length) return 0;

      const [id, order, type] = filter.sortBy.split(' | ');
      const col = Object.keys(all[0])[Number(id) - 1];
      if (!col) return 0;

      if (a[col] === b[col]) return 0;
      else if (a[col] === null) return 1;
      else if (b[col] === null) return -1;

      let valueA, valueB;

      if (type === 'text' || type === 'date' || type === "lookup") {
        valueA = a[col];
        valueB = b[col];
      } else if (type === "bool" || type === "threeStateBool") {
        valueA = Number(a[col]);
        valueB = Number(b[col]);
      } else if (type === 'price') {
        valueA = Number(String(a[col]).replace(',', ''));
        valueB = Number(String(b[col]).replace(',', ''));
      } else return 0;

      if (order === 'asc') return valueA < valueB ? -1 : 1;
      else return valueA < valueB ? 1 : -1;
    } catch (e) {
      return 0;
    }
  };

  const showModalHandler = (id) => {
    setModifiedUserId(id);
    setShowModal(true);
  };

  const hideModalHandler = () => setShowModal(false);

  const showModalUserHandler = () => setShowModalUser(true);

  const hideModalUserHandler = () => setShowModalUser(false);

  const submitUserHandler = (e) => {
    e.preventDefault();

    if (process.env.REACT_APP_LOCAL_DB === "true") {
      const { name, loginEmail, notifyEmail, newPassword, repeatNewPassword, role } = e.target.elements;

      if (newPassword.value.trim().length < 6) {
        notificationCtx.notification('error', TRL_Pack.errors.passwordIsTooShort);
        return;
      }

      if (newPassword.value != repeatNewPassword.value.trim()) {
        notificationCtx.notification('error', TRL_Pack.errors.passwordNotMatch);
        return;
      }  

      const data = {
        name: name.value,
        email: loginEmail.value,
        password: newPassword.value,
        roleId: role.value
      };

      if (!validateEmail(data.email) || !validateLogin(data.name)) {
        notificationCtx.notification('error', TRL_Pack.errors.invalidInput);
        return;
      }

      sendRequest({ url: 'users', method: 'POST', data }, (response) => {
        hideModalUserHandler();
        getUsers();
      });
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();

    const { password, newPassword, repeatNewPassword } = e.target.elements;
    if (
      password.value.trim().length < 6 ||
      newPassword.value.trim().length < 6 ||
      repeatNewPassword.value.trim().length < 6 ||
      newPassword.value !== repeatNewPassword.value
    ) {
      notificationCtx.notification('error', TRL_Pack.errors.invalidInput);
      return;
    }

    const data = {
      CurrentPassword: password.value,
      Password: newPassword.value
    };

    const url = `users/change-password/${modifiedUserId}`;

    sendRequest({
      url,
      method: 'PUT',
      data
    }, (response) => {
      if (response.status === 201) hideModalHandler();
    });
  };

  const handleSubmit = (evt) => {
    evt.preventDefault();
    const { name, password, email, warehouse: wh, role } = evt.target.elements;

    const roleId = roles.filter(odjRole => odjRole.Name === role.value)[0]?.RoleId || null;
    const warehouseId = warehouses.filter(w => w.Name === wh.value)[0]?.WarehouseId || null;

    const payload = {
      name: name.value,
      password: password.value,
      roleId: roleId,
      warehouseId: warehouseId,
      email: email.value,
    };

    if (Object.values(payload).includes(null)) return setWarning({ status: true, message: "write correct names" });
    else {
      setWarning({ status: false, message: "" });
      fetchMssqlApi(`/users`, { method: "POST", data: payload }, (res) => {
        closeForm();
        getUsers();
      });
    }
  };
  
  const onOpenFormClick = () => {
    const maybeHandler = openForm();
    if (typeof maybeHandler === "function") maybeHandler();
  };

  useEffect(() => localStorage.setItem("usersFilter", JSON.stringify(filter)), [filter]);

  const dataListForSearchFilter = (process.env.REACT_APP_LOCAL_DB === "false") ? (users && users.length ? users : all) : users;

  const pagedUsers = useMemo(() => {
    const from = (filter.page - 1) * rowsPerPage;
    const to = filter.page * rowsPerPage;
  return all.slice(from, to);

}, [all, filter.page, rowsPerPage]);

  return (
    <div className='p-20'>
      {form && (
        <UsersForm
          handleClose={closeForm}
          form={form}
          handleSubmit={handleSubmit}
          warning={warning}
        />
      )}

      <>
        {(process.env.REACT_APP_LOCAL_DB !== "true") && (
        <SearchFilter
          filterName="users"
          defaultFilter={defaultFilter}
          onFilterChange={(f) => setFilter(f)}
          onSettingsChange={(s) =>  setSettingsFilter(s)}
          search={getUsers}
          totalCount={all.slice((filter.page - 1) * rowsPerPage, filter.page * rowsPerPage).length}
          dataList={dataListForSearchFilter}
          forNow
        />
        )}

        {(process.env.REACT_APP_LOCAL_DB === "true") ? (
          <div>
            <div>
              <button
                className="d-block btn btn-link text-decoration-none ml-auto my-2 mr-1"
                onClick={() => showModalUserHandler()}
              >
                <i className="fas fa-plus mr-2" /> {TRL_Pack.buttons.addUser}
              </button>
            </div>

            <Table>

              <thead>
                <tr>
                  <th>{TRL_Pack.users.name}</th>
                  <th>{TRL_Pack.users.email}</th>
                  <th>{TRL_Pack.users.role}</th>
                  <th></th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {users.map(user => (
                  <User
                    key={user.UserId}
                    id={user.UserId}
                    name={user.Name}
                    email={user.Email}
                    isActive={user.IsActive}
                    role={roles.map(role => {
                      if (user.RoleId == role.RoleId) {
                        return role.Name;
                      }
                      return null;
                    })}
                    users={getUsers}
                    showModalHandler={showModalHandler}
                  />
                ))}
              </tbody>
            </Table>
          </div>

        ) : (

          <div className='card'>
            <div className='card-header'>
              <button
                className="d-block btn text-nowrap btn-outline-secondary text-decoration-none ml-auto my-2 mr-1"
                onClick={onOpenFormClick}
              >
                <i className="fas fa-plus" />
                {TRL_Pack.locations.addProductButton}
              </button>
            </div>
            <UsersTable
              sortRows={sortRows}
              data={pagedUsers}
              filter={filter}
              id="UserId"
              roles={roles}
              warehouses={warehouses}
              setData={getAll}
            />
          </div>
        )}
      </>
      {showModal && (process.env.REACT_APP_LOCAL_DB === "true") && (
        <Modal
          hideModalHandler={hideModalHandler}
          header={'Zmień hasło'}
        >
          <form onSubmit={submitHandler} id='modal-form'>
            <div className='form-group'>
              <label className='h6'>{TRL_Pack.users.currentPassword}</label>
              <input
                type='password'
                name='password'
                className='form-control'
                required
              />
            </div>
            <div className='form-group'>
              <label className='h6'>{TRL_Pack.users.newPassword}</label>
              <input
                type='password'
                name='newPassword'
                className='form-control'
                required
              />
            </div>
            <div className='form-group'>
              <label className='h6'>{TRL_Pack.users.repeatNewPassword}</label>
              <input
                type='password'
                name='repeatNewPassword'
                className='form-control'
                required
              />
            </div>
          </form>
        </Modal>
      )}

      {process.env.REACT_APP_LOCAL_DB === "false" ?
        (
          showModalUser && (
            <Modal
              hideModalHandler={hideModalUserHandler}
              header={TRL_Pack.users.newUser}
            >
              <form onSubmit={submitUserHandler} autoComplete="off" id='modal-form'>
                <div className='form-group'>
                  <label className='h6'>{TRL_Pack.users.name}</label>
                  <input
                    type='text'
                    name='name'
                    className='form-control'
                    maxLength={128}
                    required
                  />
                </div>
                <div className='form-group'>
                  <label className='h6'>{TRL_Pack.users.loginEmail}</label>
                  <input
                    type='text'
                    name='loginEmail'
                    className='form-control'
                    maxLength={255}
                    required
                  />
                </div>
                <div className='form-group'>
                  <label className='h6'>{TRL_Pack.users.notifyEmail}</label>
                  <input
                    type='text'
                    name='notifyEmail'
                    className='form-control'
                    maxLength={255}
                  />
                </div>
                <div className='form-group'>
                  <label className='h6'>{TRL_Pack.users.newPassword}</label>
                  <input
                    type='password'
                    name='newPassword'
                    className='form-control'
                    required
                  />
                </div>
                <div className='form-group'>
                  <label className='h6'>{TRL_Pack.users.repeatNewPassword}</label>
                  <input
                    type='password'
                    name='repeatNewPassword'
                    className='form-control'
                    required
                  />
                </div>
                <div className='form-group'>
                  <label className='h6'>{TRL_Pack.users.role}</label>
                  <select className="form-control" name="role">
                    {roles.map(role => (
                      <option key={role.RoleId} value={role.RoleId}>
                        {role.Name}
                      </option>
                    ))}
                  </select>
                </div>
              </form>
            </Modal>
          )
        )
        : (
          showModalUser && (
            <Modal
              hideModalHandler={hideModalUserHandler}
              header={TRL_Pack.users.newUser}
            >
              <form onSubmit={submitUserHandler} autoComplete="off" id='modal-form'>
                <div className='form-group'>
                  <label className='h6'>{TRL_Pack.users.name}</label>
                  <input
                    type='text'
                    name='name'
                    className='form-control'
                    maxLength={128}
                    required
                  />
                </div>
                <div className='form-group'>
                  <label className='h6'>{TRL_Pack.users.loginEmail}</label>
                  <input
                    type='text'
                    name='loginEmail'
                    className='form-control'
                    maxLength={255}
                    required
                  />
                </div>
                <div className='form-group'>
                  <label className='h6'>{TRL_Pack.users.newPassword}</label>
                  <input
                    type='password'
                    name='newPassword'
                    className='form-control'
                    required
                  />
                </div>
                <div className='form-group'>
                  <label className='h6'>{TRL_Pack.users.repeatNewPassword}</label>
                  <input
                    type='password'
                    name='repeatNewPassword'
                    className='form-control'
                    required
                  />
                </div>
                <div className='form-group'>
                  <label className='h6'>{TRL_Pack.users.role}</label>
                  <select className="form-control" name="role">
                    {roles.map(role => (
                      <option key={role.RoleId} value={role.RoleId}>
                        {role.Name}
                      </option>
                    ))}
                  </select>
                </div>
              </form>
            </Modal>
          )
        )
      }
    </div>
  );
};

export default Users;
